/* eslint-disable no-underscore-dangle */
import { isIterativePath } from '../helper/page-utility/page.utility';
import { PageInstance, PageService } from './page.service';
import { Workflow } from './workflow.type';

/**
 * This is a helper class to order the pages in a workflow based on page iteration id
 * As an example consider following configuration in a workflow
 * "pages": ["A", "B",  "X-#",            "Y-#",          "Z-#",          "C"]
 * Lets assume the size  page iterations is 2
 * In an un-ordered pages, the result will be as following
 *           "A", "B",   "X1", "X2",      "Y1", "Y2",      "Z1", "Z2",     C
 *
 * In a scenario that pages should be ordered based page iteration id the result will be
 *           "A", "B",   "X1", "Y1", "Z1",       "X2", "Y2", "Z2",          C
 *
 * In a scenario, if pageId added to ignoreList then that page will be ignored
 * Example: ignorePageOrder:["Z-#"]
 *            "A", "B",  "X1", "Y1",       "X2", "Y2",     "Z1","Z2"       C
 */
export class FlowPageOrder {
  /**
   * List of all pages ids including iterative pages
   */
  private readonly _allPagesIds: string[] = [];

  /**
   * List of all iterative page object if there is any
   */
  private readonly _iterativePages: PageInstance[] = [];
  /**
   *  List of pages to ignore the page ordering
   */
  private readonly _ignoredPages: string[] = [];

  constructor(private readonly pageService: PageService, private readonly flowConfig: Workflow, private readonly flowId: string) {
    this._allPagesIds = flowConfig.pages;
    this._ignoredPages = flowConfig.ignorePageOrder || [];
    this._allPagesIds
      .filter((pid) => isIterativePath(pid) && !this._ignoredPages?.includes(pid))
      .forEach((pageId) => {
        this._iterativePages.push(...this.pageService.pageBuilder(pageId, flowId));
      });
  }

  public pagesWithOrder(): PageInstance[] {
    return this.flowConfig.pageOrder === 'PageIteration' ? this.orderedPages() : this.unOrderedPages();
  }

  /**
   * Return list of pages without order
   * "pages": ["A", "B", "X-#", "Y-#", "Z-#", "C"]*
   * "A", "B",    "X1", "X2",     "Y1", "Y2",     "Z1", "Z2",      C
   */
  private unOrderedPages(): PageInstance[] {
    const pages: PageInstance[] = [];
    this._allPagesIds.forEach((pageId) => {
      pages.push(...this.pageService.pageBuilder(pageId, this.flowId));
    });

    return pages;
  }

  /**
   * Return list of pages whit order based on page iteration id
   * "pages": ["A", "B", "X-#", "Y-#", "Z-#", "C"]*
   * "A", "B",    "X1", "Y1", "Z1",      "X2", "Y2", "Z2",     C
   */
  private orderedPages(): PageInstance[] {
    const pages: PageInstance[] = [];
    const groupedIterativePages = this.groupedIterativePages();
    this._allPagesIds.forEach((pageId) => {
      if (isIterativePath(pageId) && groupedIterativePages.length && !this._ignoredPages.includes(pageId)) {
        pages.push(...groupedIterativePages);
        groupedIterativePages.length = 0;
      } else if (!isIterativePath(pageId) || this._ignoredPages.includes(pageId)) {
        pages.push(...this.pageService.pageBuilder(pageId, this.flowId));
      }
    });

    return pages;
  }

  /**
   * Return only list of iterative pages grouped by iteration id
   * "pages": ["A", "B", "X-#", "Y-#", "Z-#", "C"]
   * "X1", "Y1", "Z1",      "X2", "Y2", "Z2"
   */
  private groupedIterativePages(): PageInstance[] {
    const result: PageInstance[][] = [];
    for (const page of this._iterativePages) {
      if (page.pageIterationId === undefined) {
        continue;
      }

      if (!result[page.pageIterationId]) {
        result[page.pageIterationId] = [];
      }
      result[page.pageIterationId].push(page);
    }

    return result.length ? result.reduce((previous, current) => previous.concat(current)) : [];
  }
}
